import Cookies from 'js-cookie';
import { nowISOString } from './date';

/** Name of the first-party cookie holding the visitor's cookie-consent decision. */
const CONSENT_COOKIE_NAME = 'cookie-consent';

/**
 * Version of the cookie notice the visitor agreed to. Bump this whenever the notice
 * changes materially — consent covers only the terms it was given under, so a stored
 * decision against an older version is treated as absent and the visitor is asked again.
 */
const CONSENT_NOTICE_VERSION = 1;

/**
 * How long a recorded consent stays valid before the visitor is asked again.
 *
 * The GDPR sets no explicit figure. CNIL's cookie guidance — the reference most EU
 * regulators align with — caps the lifetime of consent-related cookies at 13 months and
 * recommends renewing consent every 6 months. We take the shorter of the two.
 */
const CONSENT_RETENTION_DAYS = 183;

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A consent decision as persisted in the browser. Written to and read back from a cookie,
 * so every field is validated on read rather than trusted.
 */
export interface CookieConsentRecord {
  /** Version of the cookie notice that was accepted. */
  noticeVersion: number;
  /**
   * ISO 8601 timestamp of when consent was given. Retained so the age of the decision can
   * be checked, and so the platform can evidence consent under GDPR Art. 7(1).
   */
  grantedAt: string;
}

/**
 * Cookie attributes used for every write of the consent record.
 *
 * Built per call rather than held as a constant because `secure` depends on how the page
 * was served, which is not known at module load in every entry point.
 */
function buildConsentCookieAttributes(): Cookies.CookieAttributes {
  return {
    // Without an explicit expiry the browser stores a session cookie and the notice
    // returns on the next browser start.
    expires: CONSENT_RETENTION_DAYS,
    path: '/',
    // The record is only ever read by this site. `Lax` keeps it out of third-party
    // contexts, which browsers increasingly block outright — `None` risked the cookie
    // being dropped and the notice reappearing on every visit.
    sameSite: 'Lax',
    // Browsers reject `Secure` cookies sent over plain http, which is how the app is
    // served in local development.
    secure: window.location.protocol === 'https:'
  };
}

function parseConsentRecord(storedValue: string): CookieConsentRecord | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(storedValue);
  } catch {
    // Records written before versioning held the bare string `accepted`. They carry no
    // date, so their age cannot be checked and consent is requested again.
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;

  const { noticeVersion, grantedAt } = parsed as Partial<CookieConsentRecord>;
  if (typeof noticeVersion !== 'number' || typeof grantedAt !== 'string') return null;

  return { noticeVersion, grantedAt };
}

function isWithinRetentionPeriod(grantedAt: string): boolean {
  const grantedTime = Date.parse(grantedAt);
  if (Number.isNaN(grantedTime)) return false;

  const ageMs = Date.now() - grantedTime;
  // A future-dated record means a tampered or badly skewed clock; its age is meaningless.
  if (ageMs < 0) return false;

  return ageMs <= CONSENT_RETENTION_DAYS * MILLISECONDS_PER_DAY;
}

/**
 * Reads the stored consent decision.
 *
 * @returns The stored record, or null when nothing was stored, the stored value is not a
 * record this version understands, or the decision has aged past the retention period.
 */
export function findCookieConsent(): CookieConsentRecord | null {
  const storedValue = Cookies.get(CONSENT_COOKIE_NAME);
  if (!storedValue) return null;

  const record = parseConsentRecord(storedValue);
  if (!record) return null;
  if (record.noticeVersion !== CONSENT_NOTICE_VERSION) return null;
  if (!isWithinRetentionPeriod(record.grantedAt)) return null;

  return record;
}

/**
 * Whether the visitor has a consent decision on record that still stands, and so should
 * not be shown the cookie notice.
 */
export function hasCookieConsent(): boolean {
  return findCookieConsent() !== null;
}

/**
 * Stores the visitor's acceptance of the current cookie notice, stamped with the time it
 * was given. Overwrites any earlier decision.
 */
export function recordCookieConsent(): void {
  const record: CookieConsentRecord = {
    noticeVersion: CONSENT_NOTICE_VERSION,
    grantedAt: nowISOString()
  };

  Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(record), buildConsentCookieAttributes());
}
